import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { createAndLoginDemoAccount } from "features/start/demo-session";
import { DEMO_ROUTE_PATH } from "features/start/start-flags";
import { prefersReducedMotion } from "features/start/processing-script";
import "./Auth.css";

const BOOT_PHRASE_MS = 2200;

const DemoBoot = () => {
  const { t } = useTranslation();
  const phrases = [t("auth.demoBootOpening"), t("auth.demoBootRoutes"), t("auth.demoBootSignIn")];
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    let fadeTimer;
    const tick = setInterval(() => {
      setVisible(false);
      fadeTimer = setTimeout(() => {
        setIndex((current) => (current + 1) % phrases.length);
        setVisible(true);
      }, 280);
    }, BOOT_PHRASE_MS);
    return () => {
      clearInterval(tick);
      clearTimeout(fadeTimer);
    };
  }, [phrases.length]);

  return (
    <div className="demo-boot">
      <p className="demo-boot__kicker">{t("auth.demoBootKicker")}</p>
      <h1 className="demo-boot__title">{t("auth.preparingDemo")}</h1>
      <div className="demo-boot__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100}>
        <span />
      </div>
      <p className={`demo-boot__phrase${visible ? " is-on" : ""}`} role="status" aria-live="polite">
        {phrases[index]}
      </p>
    </div>
  );
};

/**
 * Demo Page - Proxy page for landing page to trigger demo login
 * This page automatically creates a demo account and logs in the user
 */
const Demo = () => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (auth.isLoggedIn) {
      navigate(DEMO_ROUTE_PATH, { replace: true });
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    const loginWithDemo = async () => {
      setError(null);
      try {
        await createAndLoginDemoAccount({
          sendRequest,
          login: auth.login,
        });
        navigate(DEMO_ROUTE_PATH, { replace: true });
      } catch (err) {
        startedRef.current = false;
        setError(err?.message || t("anErrorOccurred"));
      }
    };

    loginWithDemo();
  }, [auth.isLoggedIn, auth.login, navigate, sendRequest, t]);

  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      margin: "auto",
      height: "calc(100vh - 12rem)",
    },
    content: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
      maxWidth: "400px",
      justifyContent: "center",
      alignItems: "center",
    },
    heading: {
      fontSize: "1.5rem",
      fontWeight: 500,
      marginBottom: "0.5rem",
      color: "#333",
    },
    paragraph: {
      fontSize: "1rem",
      color: "#666",
    },
    errorMessage: {
      color: "#dc3545",
      background: "#fff5f5",
      padding: "1rem",
      borderRadius: "8px",
      margin: "1rem 0",
      border: "1px solid #ffebeb",
    },
    retryButton: {
      background: "#4F46E5",
      color: "white",
      border: "none",
      padding: "0.625rem 1.25rem",
      borderRadius: "6px",
      fontSize: "0.875rem",
      cursor: "pointer",
      marginRight: "0.5rem",
    },
    loginButton: {
      background: "transparent",
      color: "#666",
      border: "1px solid #ddd",
      padding: "0.625rem 1.25rem",
      borderRadius: "6px",
      fontSize: "0.875rem",
      cursor: "pointer",
    },
  };

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <h1 style={styles.heading}>{t("auth.demoError")}</h1>
          <p style={styles.errorMessage}>{error}</p>
          <div>
            <button style={styles.retryButton} onClick={() => window.location.reload()}>
              {t("auth.tryAgain")}
            </button>
            <button style={styles.loginButton} onClick={() => navigate("/login")}>
              {t("auth.goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <DemoBoot />
    </div>
  );
};

export default Demo;
